import React, { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ADMIN_COLORS } from '@/constants/admin';
import { ActionButtons } from '@/components';
import { useCreateResourceMutation, convertToFormData } from '@/store/api/admin/resourcesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useCreateResourceForm } from '@/components/admin/resources/CreateResource/hooks/useCreateResourceForm';
import { IFile, IResourceFormValues } from '@/types/admin/resources';
import { normalizeFilesForUpload } from '@/utils/admin/files';
import { 
  ResourceDetailsSection,
  ResourceContentSection,
  ResourceFilesSection,
} from '@/components/admin/resources/CreateResource/components';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';

interface DesktopCreateResourcePanelProps {
  visible: boolean;
  onClose: () => void;
  highestPosition: number | undefined;
  onSuccess?: () => void;
}

export const DesktopCreateResourcePanel: React.FC<DesktopCreateResourcePanelProps> = ({
  visible,
  onClose,
  highestPosition,
  onSuccess,
}) => {
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const [createResource, { isLoading }] = useCreateResourceMutation();
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
  } = useCreateResourceForm((highestPosition !== undefined ? highestPosition + 1 : 0));

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    if (!selectedPropertyId) return;
    try {
      const normalizedFiles: IFile[] = await normalizeFilesForUpload(formData.files || []);
      const adjusted: IResourceFormValues = {
        ...formData,
        files: normalizedFiles,
      } as unknown as IResourceFormValues;
      const fd = convertToFormData(adjusted, selectedPropertyId);
      const result = await createResource(fd).unwrap();
      if (result?.success !== false) {
        showSuccess('Resource created successfully!');
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      showApiError(err, 'Failed to create resource');
    }
  }, [validateForm, selectedPropertyId, formData, createResource, onClose, showSuccess, showApiError]);

  const handleCancel = useCallback(() => {
    // Desktop web confirm instead of native Alert
    setShowDiscardModal(true);
  }, []);

  return (
    <DesktopSidePanel visible={visible} onClose={handleCancel} width={520}>
      <WebConfirmModal
        visible={showDiscardModal}
        title={'Discard Changes'}
        message={'Are you sure you want to discard your changes?'}
        confirmText={'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={() => {
          setShowDiscardModal(false);
          onClose();
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
      <View style={styles.container}>
        <DesktopPanelHeader title="New Resource" onClose={handleCancel} />
        <DesktopPanelBody scroll bottomInset={75}>
          <ResourceDetailsSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
          <ResourceContentSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
          <ResourceFilesSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
        </DesktopPanelBody>
        <View style={styles.footer}>
          <ActionButtons isLoading={isLoading} onCancel={handleCancel} onSubmit={handleSubmit} submitText="Create" />
        </View>
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});


